import { z } from "zod";

export enum NotificationRecipientLevel {
	Customer = "Customer",
	Shipment = "Shipment",
}

// GET /api/shipmentnotifications/types response item
export const notificationTypeSchema = z.object({
	id: z.number(),
	typeName: z.string(),
	description: z.string(),
});

export type NotificationType = z.infer<typeof notificationTypeSchema>;

// Internal: GET /api/shipmentnotifications/recipients response item
export const notificationRecipientSchema = z.object({
	id: z.string(),
	customerId: z.number(),
	firstName: z.string().nullable(),
	lastName: z.string().nullable(),
	email: z.string(),
	subscribedNotificationTypeIds: z.array(z.number()),
	subscribedToAll: z.boolean(),
});

export type NotificationRecipient = z.infer<typeof notificationRecipientSchema>;

// External: GET /api/customer/shipment-notifications/recipients response item
export const externalNotificationRecipientSchema = z.object({
	id: z.string(),
	name: z.string(),
	email: z.string(),
	subscribedToAll: z.boolean(),
	subscribedNotificationTypeIds: z.array(z.number()),
});

export type ExternalNotificationRecipient = z.infer<
	typeof externalNotificationRecipientSchema
>;

// Unified recipient type used throughout the UI
export type UnifiedNotificationRecipient = {
	id: string;
	firstName: string | null;
	lastName: string | null;
	email: string;
	subscribedNotificationTypeIds: number[];
	subscribedToAll: boolean;
};

export const toUnifiedRecipient = (
	recipient: NotificationRecipient,
): UnifiedNotificationRecipient => ({
	id: recipient.id,
	firstName: recipient.firstName,
	lastName: recipient.lastName,
	email: recipient.email,
	subscribedNotificationTypeIds: recipient.subscribedNotificationTypeIds,
	subscribedToAll: recipient.subscribedToAll,
});

export const externalToUnifiedRecipient = (
	recipient: ExternalNotificationRecipient,
): UnifiedNotificationRecipient => {
	const parts = recipient.name.trim().split(/\s+/);
	const firstName = parts[0] ?? "";
	const lastName = parts.slice(1).join(" ");
	return {
		id: recipient.id,
		firstName,
		lastName,
		email: recipient.email,
		subscribedNotificationTypeIds: recipient.subscribedNotificationTypeIds,
		subscribedToAll: recipient.subscribedToAll,
	};
};

// Internal: GET /api/shipmentnotifications/recipients/individual response item
export const individualNotificationRecipientSchema =
	notificationRecipientSchema.extend({
		level: z.nativeEnum(NotificationRecipientLevel),
	});

export type IndividualNotificationRecipient = z.infer<
	typeof individualNotificationRecipientSchema
>;

// External: GET /api/customer/shipment-notifications/recipients/individual response item
export const externalIndividualNotificationRecipientSchema =
	externalNotificationRecipientSchema.extend({
		recipientLevel: z.nativeEnum(NotificationRecipientLevel),
	});

export type ExternalIndividualNotificationRecipient = z.infer<
	typeof externalIndividualNotificationRecipientSchema
>;

// Unified individual recipient type (includes level for UI read-only logic)
export type UnifiedIndividualNotificationRecipient =
	UnifiedNotificationRecipient & {
		level: NotificationRecipientLevel;
	};

export const toUnifiedIndividualRecipient = (
	recipient: IndividualNotificationRecipient,
): UnifiedIndividualNotificationRecipient => ({
	...toUnifiedRecipient(recipient),
	level: recipient.level,
});

export const externalToUnifiedIndividualRecipient = (
	recipient: ExternalIndividualNotificationRecipient,
): UnifiedIndividualNotificationRecipient => ({
	...externalToUnifiedRecipient(recipient),
	level: recipient.recipientLevel,
});

export const bulkNotificationRecipientInputSchema = z.object({
	firstName: z.string().nullable(),
	lastName: z.string().nullable(),
	email: z.string().email(),
	notificationTypeIds: z.array(z.number()),
});

export type BulkNotificationRecipientInput = z.infer<
	typeof bulkNotificationRecipientInputSchema
>;

export const bulkNotificationUnsubscribeInputSchema = z.object({
	email: z.string().email(),
	notificationTypeIds: z.array(z.number()),
});

export type BulkNotificationUnsubscribeInput = z.infer<
	typeof bulkNotificationUnsubscribeInputSchema
>;

/** Subscription delta applied to every shipment in a bulk tender. */
export type BulkNotificationDelta = {
	subscribe: BulkNotificationRecipientInput[];
	unsubscribe: BulkNotificationUnsubscribeInput[];
};

export const bulkNotificationTargetSchema = z.object({
	customerId: z.number().int().positive().nullable(),
	poNumber: z.number().int().positive(),
});

export type BulkNotificationTarget = z.infer<
	typeof bulkNotificationTargetSchema
>;

/** Subscription delta applied only to the supplied customer shipments. */
export type ScopedBulkNotificationDelta = BulkNotificationDelta & {
	targets: BulkNotificationTarget[];
};

export const internalBulkNotificationShipmentSchema = z.object({
	customerId: z.number().int().positive(),
	poNumber: z.number().int().positive(),
	recipients: individualNotificationRecipientSchema.array(),
});

export const externalBulkNotificationShipmentSchema = z.object({
	poNumber: z.number().int().positive(),
	recipients: externalIndividualNotificationRecipientSchema.array(),
});

export type BulkNotificationShipment = {
	customerId: number | null;
	poNumber: number;
	recipients: UnifiedIndividualNotificationRecipient[];
};

// Internal: PUT /api/shipmentnotifications/recipients/individual request body
export const saveIndividualRecipientsBodySchema = z.object({
	poNumber: z.number(),
	customerId: z.number(),
	recipients: z.array(
		z.object({
			id: z.string().nullable(),
			firstName: z.string().nullable(),
			lastName: z.string().nullable(),
			email: z.string().email(),
			notificationTypeIds: z.array(z.number()),
		}),
	),
});

export type SaveIndividualRecipientsBody = z.infer<
	typeof saveIndividualRecipientsBodySchema
>;

// External: PUT /api/customer/shipment-notifications/recipients/individual request body
export const externalSaveIndividualRecipientsBodySchema = z.object({
	poNumber: z.number(),
	recipients: z.array(
		z.object({
			id: z.string().nullable(),
			firstName: z.string().nullable(),
			lastName: z.string().nullable(),
			email: z.string().email(),
			notificationTypeIds: z.array(z.number()),
		}),
	),
});

export type ExternalSaveIndividualRecipientsBody = z.infer<
	typeof externalSaveIndividualRecipientsBodySchema
>;

// Internal: POST /api/shipmentnotifications/recipients request body
export const createRecipientBodySchema = z.object({
	customerId: z.number(),
	firstName: z.string().nullable(),
	lastName: z.string().nullable(),
	email: z.string().email(),
	notificationTypeIds: z.array(z.number()),
});

export type CreateRecipientBody = z.infer<typeof createRecipientBodySchema>;

// External: POST /api/customer/shipment-notifications/recipients request body
export const externalCreateRecipientBodySchema = z.object({
	firstName: z.string().nullable(),
	lastName: z.string().nullable(),
	email: z.string().email(),
	notificationTypeIds: z.array(z.number()),
});

export type ExternalCreateRecipientBody = z.infer<
	typeof externalCreateRecipientBodySchema
>;

// PUT /api/shipmentnotifications/recipients/:id request body (same for internal and external)
export const updateRecipientBodySchema = z.object({
	firstName: z.string().nullable(),
	lastName: z.string().nullable(),
	email: z.string().email(),
	notificationTypeIds: z.array(z.number()),
});

export type UpdateRecipientBody = z.infer<typeof updateRecipientBodySchema>;
